import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  CloudUpload,
  Clock,
  Pencil,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
} from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { decode } from 'he';
import { PlatformIcon } from '../common/PlatformIcon';
import { SocialPost, Platform, TimeSlot, OrganizedPosts, Topic } from '../../types';
import { cn } from '../ui/utils';
import { SocialPostPreview } from './SocialPostPreview';

interface PostStatus {
  status: 'published' | 'uploaded' | 'scheduled' | 'draft';
  label: string;
  color: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
}

interface PostWithLocalDate extends SocialPost {
  localDate: Date;
}

interface GroupedPost {
  post: PostWithLocalDate;
  groupSize: number;
  indexInGroup: number;
}

const calculateRelativePosition = (postTime: Date): string => {
  const minutes = postTime.getMinutes();
  const slotStart = Math.floor(minutes / 15) * 15;
  const relativeMinutes = minutes - slotStart;
  const percentage = (relativeMinutes / 15) * 100;
  return `${percentage}%`;
};

const truncateText = (text: string | undefined, limit: number = 30): string => {
  if (!text) return '';
  const decoded = decode(text);
  return decoded.length > limit ? decoded.substring(0, limit) + '...' : decoded;
};

const groupPostsByTime = (slotPosts: PostWithLocalDate[]): GroupedPost[] => {
  if (slotPosts.length === 0) return [];

  const postsWithPosition = slotPosts.map((post) => ({
    post,
    topPosition: calculateRelativePosition(post.localDate),
  }));

  const groups: Array<Array<{ post: PostWithLocalDate; topPosition: string }>> = [];
  const POSITION_TOLERANCE = 1;

  postsWithPosition.forEach(({ post, topPosition }) => {
    let foundGroup = false;
    for (let group of groups) {
      const groupTopPosition = group[0].topPosition;
      const topPositionNum = parseFloat(topPosition);
      const groupTopNum = parseFloat(groupTopPosition);

      if (Math.abs(topPositionNum - groupTopNum) < POSITION_TOLERANCE) {
        group.push({ post, topPosition });
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      groups.push([{ post, topPosition }]);
    }
  });

  groups.sort((a, b) => {
    const aTop = parseFloat(a[0].topPosition);
    const bTop = parseFloat(b[0].topPosition);
    return aTop - bTop;
  });

  return groups.flatMap((group) =>
    group.map((item, index) => ({
      post: item.post,
      groupSize: group.length,
      indexInGroup: index,
    }))
  );
};

const getPlatformColor = (platform: string | undefined): string => {
  const colors: Record<string, string> = {
    facebook: '#1877F2',
    instagram: '#E4405F',
    linkedin: '#0A66C2',
    twitter: '#1DA1F2',
    x: '#000000',
    reddit: '#FF4500',
    threads: '#000000',
    whatsapp: '#25D366',
    bluesky: '#0560FF',
  };
  return colors[platform?.toLowerCase() || ''] || '#666666';
};

const getPostStatus = (post: SocialPost): PostStatus => {
  if (post.platformPostId && post.socialPublishDate) {
    return {
      status: 'published',
      label: 'Published',
      color: '#4caf50',
      icon: CheckCircle,
    };
  }

  if (post.platformPostId && !post.socialPublishDate) {
    return {
      status: 'uploaded',
      label: 'Uploaded',
      color: '#2196f3',
      icon: CloudUpload,
    };
  }

  if (post.scheduleTime && !post.platformPostId && !post.socialPublishDate) {
    return {
      status: 'scheduled',
      label: 'Scheduled',
      color: '#ff9800',
      icon: Clock,
    };
  }

  if (
    post.isDraft === true ||
    (!post.scheduleTime && !post.platformPostId && !post.socialPublishDate)
  ) {
    return {
      status: 'draft',
      label: 'Draft',
      color: '#9e9e9e',
      icon: Pencil,
    };
  }

  return {
    status: 'draft',
    label: 'Draft',
    color: '#9e9e9e',
    icon: Pencil,
  };
};

interface CalendarDayViewProps {
  day: Date;
  timeSlots: TimeSlot[];
  posts: OrganizedPosts;
  onTimeSlotDrop?: (topic: Topic, scheduledDate: Date) => void;
  onPostClick?: (post: SocialPost) => void;
  onCellClick?: (scheduledDate: Date) => void;
  selectedScheduleTime?: Date | string | number;
  onScrollToCurrentTimeReady?: (scrollFn: () => void) => void;
  onRefresh?: () => void;
  onPreviousDay?: () => void;
  onNextDay?: () => void;
  onScrollToCurrentTime?: () => void;
}

export function CalendarDayView({
  day,
  timeSlots,
  posts,
  onTimeSlotDrop,
  onPostClick,
  onCellClick,
  selectedScheduleTime,
  onScrollToCurrentTimeReady,
  onRefresh,
  onPreviousDay,
  onNextDay,
  onScrollToCurrentTime,
}: CalendarDayViewProps) {
  const currentTimeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef<boolean>(false);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState<boolean>(false);
  const [draggedOverSlot, setDraggedOverSlot] = useState<string | null>(null);
  const [hoveredPost, setHoveredPost] = useState<PostWithLocalDate | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Update current time every 15 seconds for smoother updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, []);

  // Calculate current time slot index
  const getCurrentTimeSlotIndex = (): number => {
    const currentTimeString = format(currentTime, 'HH:mm');
    const slotIndex = timeSlots.findIndex((slot) => {
      const slotTime = format(slot.date, 'HH:mm');
      return slotTime >= currentTimeString;
    });
    return slotIndex === -1 ? timeSlots.length - 1 : slotIndex;
  };

  const isViewingToday = isSameDay(day, currentTime);
  const currentSlotIndex = isViewingToday ? getCurrentTimeSlotIndex() : -1;

  // Find scrollable parent helper
  const findScrollableParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    let scrollableParent: HTMLElement | null = element.parentElement;
    while (scrollableParent) {
      const style = window.getComputedStyle(scrollableParent);
      // Check computed style first
      if (
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflow === 'scroll'
      ) {
        return scrollableParent;
      }
      // Also check class names
      if (
        scrollableParent.classList.contains('overflow-y-auto') ||
        scrollableParent.classList.contains('overflow-auto') ||
        scrollableParent.classList.contains('overflow-x-auto')
      ) {
        return scrollableParent;
      }
      scrollableParent = scrollableParent.parentElement;
      // Stop at body to avoid going too far up
      if (scrollableParent === document.body || scrollableParent === null) {
        break;
      }
    }
    return null;
  }, []);

  // Scroll to current time function
  const scrollToCurrentTime = useCallback(() => {
    // Set flags IMMEDIATELY and synchronously to prevent any hover from being set
    // This must happen before any async operations
    setHoveredPost(null);
    isProgrammaticScrollRef.current = true;
    setIsProgrammaticScroll(true);

    // Use a small timeout to ensure DOM is fully ready, then requestAnimationFrame
    setTimeout(() => {
      requestAnimationFrame(() => {
        // Get fresh current time
        const now = new Date();
        const currentTimeString = format(now, 'HH:mm');
        const viewingToday = isSameDay(day, now);

        // If not viewing today, don't scroll (parent should handle date navigation)
        if (!viewingToday) {
          // Reset flags if we're not scrolling
          isProgrammaticScrollRef.current = false;
          setIsProgrammaticScroll(false);
          return;
        }

        // Calculate the current slot index
        const slotIndex = timeSlots.findIndex((slot) => {
          const slotTime = format(slot.date, 'HH:mm');
          return slotTime >= currentTimeString;
        });
        const finalSlotIndex = slotIndex === -1 ? timeSlots.length - 1 : slotIndex;

        // Find scrollable parent - try multiple methods
        let scrollableParent: HTMLElement | null = null;

        // Method 1: Try to find via containerRef
        if (containerRef.current) {
          scrollableParent = findScrollableParent(containerRef.current);
        }

        // Method 2: If not found, try to find the overflow-y-auto container directly
        if (!scrollableParent && containerRef.current) {
          let parent: HTMLElement | null = containerRef.current.parentElement;
          while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
              scrollableParent = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }

        // Method 3: Try to find via currentTimeRef if available
        if (!scrollableParent && currentTimeRef.current) {
          scrollableParent = findScrollableParent(currentTimeRef.current);
        }

        if (!scrollableParent) {
          console.warn('Could not find scrollable parent');
          // Reset flags if we can't scroll
          isProgrammaticScrollRef.current = false;
          setIsProgrammaticScroll(false);
          return;
        }

        // Ensure hover is still cleared (redundant but safe)
        setHoveredPost(null);

        // Helper function to re-enable hover after scroll
        const reEnableHover = () => {
          // Clear hover state multiple times to catch any delayed events
          setHoveredPost(null);
          setTimeout(() => {
            setHoveredPost(null);
            isProgrammaticScrollRef.current = false;
            setIsProgrammaticScroll(false);
          }, 100);
        };

        // Try method 1: Use ref if available and viewing today
        if (viewingToday && currentTimeRef.current) {
          const containerHeight = scrollableParent.clientHeight;
          const offset = containerHeight / 4;
          const elementTop = currentTimeRef.current.offsetTop;
          scrollableParent.scrollTo({
            top: elementTop - offset,
            behavior: 'smooth',
          });
          // Re-enable hover after scroll completes (smooth scroll takes ~500ms, add extra buffer)
          setTimeout(() => {
            reEnableHover();
          }, 800);
          return;
        }

        // Try method 2: Find element by data attribute
        if (containerRef.current) {
          const timeSlotElement = containerRef.current.querySelector(
            `[data-time-slot="${finalSlotIndex}"]`
          ) as HTMLElement;

          if (timeSlotElement) {
            const containerHeight = scrollableParent.clientHeight;
            const offset = containerHeight / 4;
            const elementTop = timeSlotElement.offsetTop;
            scrollableParent.scrollTo({
              top: elementTop - offset,
              behavior: 'smooth',
            });
            // Re-enable hover after scroll completes (smooth scroll takes ~500ms, add extra buffer)
            setTimeout(() => {
              reEnableHover();
            }, 800);
            return;
          }
        }

        // Method 3: Calculate scroll position based on slot index
        // Each row is 40px, header is 48px
        const rowHeight = 40;
        const headerHeight = 48;
        const targetScrollTop =
          headerHeight + finalSlotIndex * rowHeight - scrollableParent.clientHeight / 4;
        scrollableParent.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth',
        });
        // Re-enable hover after scroll completes (smooth scroll takes ~500ms, add extra buffer)
        setTimeout(() => {
          reEnableHover();
        }, 800);
      });
    }, 50); // Small delay to ensure DOM is ready
  }, [findScrollableParent, day, timeSlots]);

  // Expose scroll function to parent - use a ref to keep it stable
  const scrollToCurrentTimeRef = useRef(scrollToCurrentTime);
  scrollToCurrentTimeRef.current = scrollToCurrentTime;

  // Expose scroll function to parent
  useEffect(() => {
    if (onScrollToCurrentTimeReady) {
      // Always pass a stable function that calls the latest version
      onScrollToCurrentTimeReady(() => {
        scrollToCurrentTimeRef.current();
      });
    }
  }, [onScrollToCurrentTimeReady]);

  // Initial scroll to current time on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      scrollToCurrentTime();
    }, 100);
    return () => clearTimeout(timer);
  }, [timeSlots, scrollToCurrentTime]);

  // Clear hover during scroll events (especially programmatic scrolls)
  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current || isProgrammaticScroll) {
        setHoveredPost(null);
      }
    };

    // Find scrollable parent and attach scroll listener
    const findAndAttachScrollListener = () => {
      if (containerRef.current) {
        const scrollableParent = findScrollableParent(containerRef.current);
        if (scrollableParent) {
          scrollableParent.addEventListener('scroll', handleScroll, { passive: true });
          return () => {
            scrollableParent.removeEventListener('scroll', handleScroll);
          };
        }
      }
      return undefined;
    };

    // Try to attach immediately, and also after a short delay in case DOM isn't ready
    const cleanup1 = findAndAttachScrollListener();
    const timer = setTimeout(() => {
      const cleanup2 = findAndAttachScrollListener();
      if (cleanup2) {
        return cleanup2;
      }
    }, 100);

    return () => {
      cleanup1?.();
      clearTimeout(timer);
    };
  }, [findScrollableParent, isProgrammaticScroll]);

  const handleDragOver = (e: React.DragEvent, dateKey: string, slotKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDraggedOverSlot(`${dateKey}-${slotKey}`);
  };

  const handleDragLeave = () => {
    setDraggedOverSlot(null);
  };

  const handleDrop = (e: React.DragEvent, day: Date, slot: TimeSlot) => {
    e.preventDefault();
    setDraggedOverSlot(null);

    const topicData = e.dataTransfer.getData('application/json');
    if (topicData) {
      try {
        const topic: Topic = JSON.parse(topicData);
        const slotTime = format(slot.date, 'HH:mm');
        const [hours, minutes] = slotTime.split(':').map(Number);

        const scheduledDate = new Date(day);
        scheduledDate.setHours(hours, minutes, 0, 0);

        if (onTimeSlotDrop) {
          onTimeSlotDrop(topic, scheduledDate);
        }
      } catch (error) {
        console.error('Error parsing dropped topic:', error);
      }
    }
  };

  const handlePostMouseEnter = (e: React.MouseEvent, post: PostWithLocalDate) => {
    // CRITICAL: Check ref FIRST (synchronous) before any state checks
    // This prevents hover from being set during programmatic scroll
    if (isProgrammaticScrollRef.current) {
      setHoveredPost(null);
      return;
    }

    // Also check state (for React re-renders)
    if (isProgrammaticScroll) {
      setHoveredPost(null);
      return;
    }

    // Only set hover if we're not in programmatic scroll
    setHoveredPost(post);
    setTimeout(() => {
      // Double-check BOTH flags before showing tooltip (in case it changed during timeout)
      if (!isProgrammaticScrollRef.current && !isProgrammaticScroll) {
        calculateTooltipPosition(e.clientX, e.clientY);
      } else {
        // Clear hover if flag was set during timeout
        setHoveredPost(null);
      }
    }, 0);
  };

  const handlePostMouseLeave = () => {
    setHoveredPost(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Clear hover if we're in the middle of a programmatic scroll
    if (isProgrammaticScrollRef.current || isProgrammaticScroll) {
      setHoveredPost(null);
      return;
    }
    if (hoveredPost) {
      calculateTooltipPosition(e.clientX, e.clientY);
    }
  };

  const calculateTooltipPosition = (mouseX: number, mouseY: number) => {
    const TOOLTIP_OFFSET = 15;
    const PREVIEW_WIDTH = 360; // Approximate width for most previews (reduced)
    const PREVIEW_HEIGHT = 400; // Approximate height (can be up to 426px for vertical formats)

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const tooltipHeight = tooltipRef.current?.offsetHeight || PREVIEW_HEIGHT;
    const tooltipWidth = tooltipRef.current?.offsetWidth || PREVIEW_WIDTH;

    // Calculate horizontal position
    let left = mouseX + TOOLTIP_OFFSET;
    if (left + tooltipWidth > viewportWidth) {
      left = mouseX - tooltipWidth - TOOLTIP_OFFSET;
      if (left < TOOLTIP_OFFSET) {
        // If it still doesn't fit, center it or align to edge
        left = Math.max(
          TOOLTIP_OFFSET,
          Math.min(viewportWidth - tooltipWidth - TOOLTIP_OFFSET, TOOLTIP_OFFSET)
        );
      }
    }

    // Calculate vertical position - ensure bottom doesn't go outside viewport
    let top = mouseY + TOOLTIP_OFFSET;

    // Check if bottom would go outside viewport
    const maxTop = viewportHeight - tooltipHeight - TOOLTIP_OFFSET;
    if (top > maxTop) {
      // Try positioning above the cursor
      top = mouseY - tooltipHeight - TOOLTIP_OFFSET;

      // If that would go above viewport, position at bottom of viewport
      if (top < TOOLTIP_OFFSET) {
        top = Math.max(TOOLTIP_OFFSET, maxTop);
      }
    }

    // Final safety check - ensure both top and bottom are within bounds
    top = Math.max(TOOLTIP_OFFSET, Math.min(top, maxTop));

    setTooltipPosition({ x: left, y: top });
  };

  const handleCellClick = (e: React.MouseEvent, day: Date, slot: TimeSlot) => {
    if ((e.target as HTMLElement).closest('[data-post-item]')) {
      return;
    }

    if (onCellClick) {
      const slotTime = format(slot.date, 'HH:mm');
      const [hours, minutes] = slotTime.split(':').map(Number);

      const scheduledDate = new Date(day);
      scheduledDate.setHours(hours, minutes, 0, 0);

      onCellClick(scheduledDate);
    }
  };

  const isCurrentTimeCell = (day: Date, slot: TimeSlot, slotIndex: number): boolean => {
    const cellDate = new Date(day);

    const isSameDay =
      currentTime.getFullYear() === cellDate.getFullYear() &&
      currentTime.getMonth() === cellDate.getMonth() &&
      currentTime.getDate() === cellDate.getDate();

    if (!isSameDay) return false;

    // Check if this slot contains the current time
    const slotTime = format(slot.date, 'HH:mm');
    const [hours, minutes] = slotTime.split(':').map(Number);
    const slotStart = new Date(cellDate);
    slotStart.setHours(hours, minutes, 0, 0);

    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 15);

    return currentTime >= slotStart && currentTime < slotEnd;
  };

  const dateKey = format(day, 'yyyy-MM-dd');

  return (
    <>
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        className="w-full grid border-t-[0.5px] border-gray-300/30 box-border"
        style={{
          gridTemplateColumns: 'minmax(68px, auto) 1fr',
          gridTemplateRows: '48px repeat(96, 40px)',
          height: '3888px',
          minHeight: '3888px',
        }}
      >
        <div className="border-r-[0.5px] border-b-[0.5px] border-gray-300/30 pr-1 h-12 bg-cmp-surface sticky top-0 z-[100] box-border flex items-center justify-center">
          {onScrollToCurrentTime && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onScrollToCurrentTime();
              }}
              className="p-1 rounded text-black hover:bg-cmp-bronze-soft transition-colors"
              title="Scroll to current time"
            >
              <CalendarClock className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="border-r-[0.5px] border-b-[0.5px] border-t-[0.5px] border-l-[0.5px] border-gray-300/30 p-1 h-12 bg-cmp-surface sticky top-0 z-[100] flex items-center justify-between box-border relative">
          <div className="flex-1"></div>
          <div className="flex items-center gap-2 absolute left-1/3 transform -translate-x-1/3">
            {onPreviousDay && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviousDay();
                }}
                className="p-1 rounded text-black hover:bg-cmp-bronze-soft transition-colors flex-shrink-0"
                title="Previous day"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <p className="text-sm font-medium text-black width-full">
              {format(day, 'EEEE - MMM d, yyyy')}
            </p>
            {onNextDay && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNextDay();
                }}
                className="p-1 rounded text-black hover:bg-cmp-bronze-soft transition-colors flex-shrink-0"
                title="Next day"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center flex-1 justify-end">
            {onRefresh && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                className="p-1 pr-3 rounded text-black hover:bg-cmp-bronze-soft transition-colors"
                title="Refresh posts and topics"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {timeSlots.map((slot, index) => (
          <React.Fragment key={slot.key}>
            <div
              ref={index === currentSlotIndex ? currentTimeRef : null}
              data-time-slot={index}
              className={cn(
                'border-r-[0.5px] border-t-[0.5px] border-gray-300/30 pr-1 flex items-start justify-end sticky left-0 bg-cmp-surface z-[9] box-border -pt-1',
                index === timeSlots.length - 1 && 'border-b-[0.5px] border-gray-300/30'
              )}
              style={{
                borderTopStyle: index % 4 === 0 ? 'solid' : 'dashed',
                borderTopWidth: index % 4 === 0 ? '0.5px' : '0.5px',
              }}
            >
              <p className="relative -top-2.5 text-xs text-black">{slot.label}</p>
            </div>

            <div
              key={`${dateKey}-${slot.key}`}
              onClick={(e) => handleCellClick(e, day, slot)}
              onDragOver={(e) => handleDragOver(e, dateKey, slot.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day, slot)}
              className={cn(
                'border-r-[0.5px] border-t-[0.5px] border-l-[0.5px] border-gray-300/30 p-1 hover:bg-cmp-bronze-soft/20 box-border relative cursor-pointer transition-colors duration-200 min-w-0 overflow-visible',
                index === timeSlots.length - 1 && 'border-b-[0.5px] border-gray-300/30'
              )}
              style={{
                borderTopStyle: index % 4 === 0 ? 'solid' : 'dashed',
                borderTopWidth: '0.5px',
                backgroundColor:
                  draggedOverSlot === `${dateKey}-${slot.key}`
                    ? 'rgba(0, 97, 254, 0.45)'
                    : isCurrentTimeCell(day, slot, index)
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  draggedOverSlot === `${dateKey}-${slot.key}`
                    ? 'rgba(0, 97, 254, 0.85)'
                    : isCurrentTimeCell(day, slot, index)
                      ? 'rgba(59, 130, 246, 0.35)'
                      : 'rgba(0, 97, 254, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  draggedOverSlot === `${dateKey}-${slot.key}`
                    ? 'rgba(0, 97, 254, 0.45)'
                    : isCurrentTimeCell(day, slot, index)
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'transparent';
              }}
            >
              {(() => {
                const slotPosts = ((posts[dateKey] && posts[dateKey][slot.key]) ||
                  []) as PostWithLocalDate[];
                const groupedPosts = groupPostsByTime(slotPosts);

                return groupedPosts.map(({ post, groupSize, indexInGroup }) => {
                  const postStatus = getPostStatus(post);
                  const widthPercentage = 100 / groupSize;
                  const leftPercentage = (indexInGroup * 100) / groupSize;

                  return (
                    <div
                      key={`post-${post.id}`}
                      data-post-item
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onPostClick) onPostClick(post);
                      }}
                      onMouseEnter={(e) => handlePostMouseEnter(e, post)}
                      onMouseLeave={handlePostMouseLeave}
                      className="px-2 py-2 h-[30px] min-h-[30px] text-white text-xs overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer absolute z-10 flex items-center box-border"
                      style={{
                        backgroundColor: getPlatformColor(post.platform),
                        top: calculateRelativePosition(post.localDate),
                        left: `${leftPercentage}%`,
                        width: `calc(${widthPercentage}% - ${groupSize > 1 ? '2px' : '0px'})`,
                        maxWidth: `calc(${widthPercentage}% - ${groupSize > 1 ? '2px' : '0px'})`,
                        flexShrink: 0,
                        marginLeft: indexInGroup > 0 ? '2px' : '0px',
                        pointerEvents: isProgrammaticScroll ? 'none' : 'auto',
                      }}
                    >
                      {(() => {
                        const StatusIcon = postStatus.icon;
                        return (
                          <div className="absolute top-0.5 right-0.5 z-[11] flex items-center justify-center">
                            <StatusIcon
                              className="text-base"
                              style={{
                                stroke: postStatus.color,
                                strokeWidth: 2,
                                fill: 'none',
                                fontSize: '16px',
                              }}
                            />
                          </div>
                        );
                      })()}
                      <div className="flex items-center gap-1 min-w-0 w-full h-full relative">
                        <div className="flex items-center flex-shrink-0">
                          <PlatformIcon platform={post.platform as Platform} size={18} />
                        </div>
                        <p className="flex-1 overflow-hidden text-ellipsis text-sm whitespace-nowrap leading-snug font-medium">
                          {truncateText(post.title || post.summary || '', 30)}
                        </p>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </React.Fragment>
        ))}
      </div>

      {hoveredPost && !isProgrammaticScroll && (
        <div
          ref={tooltipRef}
          className="fixed z-[5000] shadow-2xl pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
          onMouseEnter={(e) => e.stopPropagation()}
        >
          <SocialPostPreview post={hoveredPost} />
        </div>
      )}
    </>
  );
}
